import { DefaultChainId } from '../constants';

import { useEffect, useState } from 'react';
import { useAccount, useChainId } from 'wagmi';
import { useEthersProvider } from './useEthersProvider';
import { Contract } from 'ethers';
import { getContract } from '@/utils';
import user from '@/stores/user';

export function useContract(
    address: string | undefined,
    ABI: any,
    withSignerIfPossible = true,
): Contract | null {
    const chainId = useChainId();

    const { address: account } = useAccount();
    const provider = useEthersProvider({
        chainId: chainId || DefaultChainId,
        account,
    });

    const [contract, setContract] = useState<Contract | null>(null);

    async function init() {
        if (!address || !ABI || !provider) return null;
        try {
            const contract = await getContract(
                address,
                ABI,
                provider as any,
                withSignerIfPossible && account ? account : undefined,
            );

            setContract(contract);
        } catch (error) {
            console.error('Failed to get contract', error);
            console.error(address);
            setContract(null);
        }
    }

    useEffect(() => {
        init();
    }, [address, ABI, provider, withSignerIfPossible, account, chainId]);

    return contract;
}

export function useContractByGetContract(getContractFunc: any) {
    const { web3, chainId } = user;
    const [contract, setContract] = useState<any>();

    useEffect(() => {
        if (getContractFunc && web3 && chainId) {
            const contractInstance = getContractFunc(chainId, web3);
            setContract(contractInstance);
        } else {
            setContract(undefined);
        }
    }, [getContractFunc, web3, chainId]);

    return contract;
}

export function useGetContractByUserWeb3() {
    const chainId = useChainId();
    const { address: account } = useAccount();
    const provider = useEthersProvider({
        chainId: chainId || DefaultChainId,
        account,
    });

    async function getContract(address: string, abi: any, needSigner = true) {
        if (!provider) {
            return undefined;
        }
        try {
            let signerOrProvider: any = provider;
            if (needSigner && account) {
                // Check if provider has getSigner method (BrowserProvider, JsonRpcProvider)
                if (
                    'getSigner' in provider &&
                    typeof provider.getSigner === 'function'
                ) {
                    signerOrProvider = await provider.getSigner(account);
                }
            }
            return new Contract(address, abi, signerOrProvider);
        } catch (error) {
            console.error('Failed to get contract', error);
            return undefined;
        }
    }

    return getContract;
}
